import type { CSSProperties, ReactNode } from 'react';
import { Button, PlusIcon } from '../Button/Button';
import type { ButtonSize, ButtonVariant } from '../Button/Button.types';
import { illustrationSrc, type IllustrationSlug } from '../../tokens/illustrations';
import './EmptyState.css';

export type EmptyStateDirection = 'vertical' | 'horizontal';
/**
 * `medium` / `small` — the original list/section empty states.
 * `large` — upsell / paywall layout (Figma `9282:4193`): 18/24 semibold title,
 * 14/22 secondary body, 40px primary CTA, 16px rhythm between blocks.
 */
export type EmptyStateSize = 'medium' | 'small' | 'large';

/** Default illustration slug when `illustration` is not provided. */
export const EMPTY_STATE_DEFAULT_ILLUSTRATION: IllustrationSlug = 'illustration-box-open';

/** Illustration + message shown when a list or section has no content. Optionally includes a CTA. */
export type EmptyStateProps = {
  title?: string;
  subtitle?: string;
  direction?: EmptyStateDirection;
  size?: EmptyStateSize;
  /**
   * Registered illustration slug (see `tokens/illustrations.ts`), or an explicit image URL / path.
   * Defaults to `illustration-box-open`.
   */
  illustration?: IllustrationSlug | string;
  /** Rendered illustration box width in px. Defaults per `size` (120 / 80 / 214). */
  illustrationWidth?: number;
  /** Rendered illustration box height in px. Defaults per `size` (134 / 90 / 179). */
  illustrationHeight?: number;
  showSubtitle?: boolean;
  /**
   * Optional secondary line rendered between the subtitle and the CTA — e.g. a
   * failure reason (`Reason: …`). Omitted when empty or whitespace-only.
   */
  description?: string;
  showMainButton?: boolean;
  /** Figma "Button Main" icon toggle — instances may hide the CTA's plus icon. */
  showCtaIcon?: boolean;
  /** Explicit CTA start icon (e.g. `<SparkIcon slug="brain-icon" />`); replaces the plus icon. */
  ctaStartIcon?: ReactNode;
  /** CTA button size; defaults per `size` (`small` / `small` / `large`). */
  ctaSize?: ButtonSize;
  /**
   * Max width in px of the title / body / CTA column. Figma empty-state frames fix the
   * copy width (e.g. 368px) so the body wraps the same way as in the design.
   */
  contentWidth?: number;
  ctaLabel?: string;
  onCtaClick?: () => void;
  /** Disable the CTA while a `ReadOnlyProvider` is read-only, with its reason as a tooltip. */
  ctaDisableWhenReadOnly?: boolean;
  className?: string;
};

type CtaPreset = { size: ButtonSize; variant: ButtonVariant; showIcon: boolean };

const CTA_PRESET: Record<EmptyStateSize, CtaPreset> = {
  medium: { size: 'small', variant: 'ghost', showIcon: true },
  small: { size: 'small', variant: 'ghost', showIcon: true },
  large: { size: 'large', variant: 'primary', showIcon: false },
};

function EmptyStateIllustration({
  size,
  src,
  width,
  height,
}: {
  size: EmptyStateSize;
  src: string;
  width?: number;
  height?: number;
}) {
  const style: CSSProperties | undefined =
    width != null || height != null
      ? { width: width != null ? `${width}px` : undefined, height: height != null ? `${height}px` : undefined }
      : undefined;
  return (
    <div
      className={`spark-empty-state-illustration spark-empty-state-illustration-${size}`}
      style={style}
      aria-hidden="true"
    >
      <img src={src} alt="" />
    </div>
  );
}

export function EmptyState({
  title = 'Title',
  subtitle = 'Subtitle',
  direction = 'vertical',
  size = 'medium',
  illustration = EMPTY_STATE_DEFAULT_ILLUSTRATION,
  illustrationWidth,
  illustrationHeight,
  showSubtitle = true,
  description,
  showMainButton = false,
  showCtaIcon,
  ctaStartIcon,
  ctaSize,
  contentWidth,
  ctaLabel = 'CTA',
  onCtaClick,
  ctaDisableWhenReadOnly = false,
  className = '',
}: EmptyStateProps) {
  const rootClasses = [
    'spark-empty-state',
    `spark-empty-state-${direction}`,
    `spark-empty-state-${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const cta = CTA_PRESET[size];
  const withCtaIcon = showCtaIcon ?? cta.showIcon;
  const resolvedCtaStartIcon = ctaStartIcon ?? (withCtaIcon ? <PlusIcon size="small" /> : undefined);
  const contentStyle: CSSProperties | undefined =
    contentWidth != null ? { maxWidth: `${contentWidth}px` } : undefined;

  return (
    <div className={rootClasses}>
      <EmptyStateIllustration
        size={size}
        src={illustrationSrc(illustration)}
        width={illustrationWidth}
        height={illustrationHeight}
      />
      <div className="spark-empty-state-content" style={contentStyle}>
        <p className="spark-empty-state-title">{title}</p>
        {showSubtitle ? <p className="spark-empty-state-subtitle">{subtitle}</p> : null}
        {description?.trim() ? (
          <p className="spark-empty-state-description">{description}</p>
        ) : null}
        {showMainButton ? (
          <Button
            className="spark-empty-state-cta"
            size={ctaSize ?? cta.size}
            variant={cta.variant}
            startIcon={resolvedCtaStartIcon}
            disableWhenReadOnly={ctaDisableWhenReadOnly}
            onClick={onCtaClick}
          >
            {ctaLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
